import React from 'react';
import './button.css';

type QueryStatus = 'idle' | 'pending' | 'error' | 'success';

interface ButtonBaseProps extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'disabled'> {
  /** Visual style of the button */
  variant: 'primary' | 'secondary' | 'danger';
  /** Icon to render inside the button */
  icon?: React.ReactNode;
  /** Position of the icon relative to the label. Defaults to 'leading' */
  iconPosition?: 'leading' | 'trailing';
  /** Button size. Defaults to 'medium' */
  size?: 'large' | 'medium' | 'small';
  /** Disables the button */
  isDisabled?: boolean;
  /** Shows a loading spinner and disables the button */
  isLoading?: boolean;
  /**
   * TanStack Query or Mutation status.
   * When 'pending', activates the loading state automatically.
   * Pass the `status` field from `useQuery` or `useMutation` directly.
   */
  queryStatus?: QueryStatus;
  /** Renders as type="submit". Defaults to type="button" */
  isSubmit?: boolean;
}

type ButtonWithLabel = ButtonBaseProps & {
  /** Visible text label */
  label: string;
  'aria-label'?: string;
};

type ButtonIconOnly = ButtonBaseProps & {
  label?: never;
  /** Required for icon-only buttons for screen reader accessibility */
  'aria-label': string;
};

export type ButtonProps = ButtonWithLabel | ButtonIconOnly;

const LoadingSpinner = () => (
  <svg
    className="h-4 w-4 animate-spin"
    xmlns="http://www.w3.org/2000/svg"
    fill="none"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path
      className="opacity-75"
      fill="currentColor"
      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
    />
  </svg>
);

/**
 * Primary and secondary call-to-action button.
 *
 * For tertiary / low-emphasis actions (text links, icon-only edit pens) use TertiaryAction instead.
 *
 * @example
 * // Standard async action wired to TanStack Mutation
 * const mutation = useMutation({ mutationFn: signIn });
 * <Button variant="primary" label="Sign in" queryStatus={mutation.status} onClick={mutation.mutate} />
 *
 * @example
 * // Icon-only (aria-label required)
 * <Button variant="secondary" icon={<TrashIcon />} aria-label="Delete item" size="small" />
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    label,
    icon,
    iconPosition = 'leading',
    size = 'medium',
    isDisabled = false,
    isLoading = false,
    queryStatus,
    isSubmit = false,
    className,
    ...rest
  },
  ref,
) {
  const loading = isLoading || queryStatus === 'pending';
  const disabled = isDisabled || loading;
  const iconOnly = !label;

  const classes = [
    'btn',
    `btn--${variant}`,
    `btn--${size}`,
    iconOnly ? 'btn--icon-only' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconEl = icon ? (
    <span className="btn__icon" aria-hidden="true">
      {icon}
    </span>
  ) : null;

  const renderContent = () => {
    if (loading) {
      return (
        <span className="relative flex items-center justify-center">
          {!iconOnly && <span className="invisible">{label}</span>}
          <span className={iconOnly ? undefined : 'absolute'}>
            <LoadingSpinner />
          </span>
        </span>
      );
    }

    if (iconOnly) {
      return iconEl;
    }

    return iconPosition === 'leading' ? (
      <>
        {iconEl}
        {label}
      </>
    ) : (
      <>
        {label}
        {iconEl}
      </>
    );
  };

  return (
    <button
      {...rest}
      ref={ref}
      type={isSubmit ? 'submit' : 'button'}
      disabled={disabled}
      className={classes}
    >
      {renderContent()}
    </button>
  );
});
